<template>
  <div
    class="flex min-h-10 items-stretch border-b border-bor-nav px-2"
    data-test="chat-text-format-toolbar"
  >
    <div class="flex items-stretch gap-0">
      <div :class="formatToggleWrapClass">
        <wui-btn
          prepend-icon="txtFormatBoldM"
          variant="neut"
          state="alpha"
          :title="formatButtonTitle('chat.textFormatBold', 'bold')"
          icon
          data-test="chat-text-format-bold"
          @mousedown.capture.prevent="onFormatMouseDown('bold')"
        >
          <span class="sr-only select-none">{{ t('chat.textFormatBold') }}</span>
        </wui-btn>
        <span
          v-if="activeFormats.bold"
          class="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-comp-scroll-def"
        />
      </div>
      <div :class="formatToggleWrapClass">
        <wui-btn
          prepend-icon="txtFormatItalicM"
          variant="neut"
          state="alpha"
          :title="formatButtonTitle('chat.textFormatItalic', 'italic')"
          icon
          data-test="chat-text-format-italic"
          @mousedown.capture.prevent="onFormatMouseDown('italic')"
        >
          <span class="sr-only select-none">{{ t('chat.textFormatItalic') }}</span>
        </wui-btn>
        <span
          v-if="activeFormats.italic"
          class="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-comp-scroll-def"
        />
      </div>
      <div :class="formatToggleWrapClass">
        <wui-btn
          prepend-icon="txtFormatUnderlineM"
          variant="neut"
          state="alpha"
          :title="formatButtonTitle('chat.textFormatUnderline', 'underline')"
          icon
          data-test="chat-text-format-underline"
          @mousedown.capture.prevent="onFormatMouseDown('underline')"
        >
          <span class="sr-only select-none">{{ t('chat.textFormatUnderline') }}</span>
        </wui-btn>
        <span
          v-if="activeFormats.underline"
          class="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-comp-scroll-def"
        />
      </div>
      <div :class="formatToggleWrapClass">
        <wui-btn
          prepend-icon="txtFormatStrikethroughM"
          variant="neut"
          state="alpha"
          :title="formatButtonTitle('chat.textFormatStrikethrough', 'strikethrough')"
          icon
          data-test="chat-text-format-strikethrough"
          @mousedown.capture.prevent="onFormatMouseDown('strikethrough')"
        >
          <span class="sr-only select-none">{{ t('chat.textFormatStrikethrough') }}</span>
        </wui-btn>
        <span
          v-if="activeFormats.strikethrough"
          class="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-comp-scroll-def"
        />
      </div>
    </div>

    <div
      class="flex w-2 shrink-0 items-center justify-center py-1"
      aria-hidden="true"
    >
      <div class="h-full w-px bg-bor-nav" />
    </div>

    <div class="flex items-stretch gap-0">
      <div :class="formatToggleWrapClass">
        <wui-btn
          prepend-icon="linkM"
          variant="neut"
          state="alpha"
          :title="formatButtonTitle('chat.textFormatLink', 'link')"
          icon
          data-test="chat-text-format-link"
          @mousedown.capture.prevent="emit('prepare')"
          @click="emit('link')"
        >
          <span class="sr-only select-none">{{ t('chat.textFormatLink') }}</span>
        </wui-btn>
        <span
          v-if="activeFormats.link"
          class="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-comp-scroll-def"
        />
      </div>
      <!-- TODO WUI-5429: numbered list
      <wui-btn
        prepend-icon="listNumbersM"
        variant="neut"
        state="alpha"
        icon
      >
        <span class="sr-only select-none">{{ t('chat.textFormatNumberedList') }}</span>
      </wui-btn>
      -->
      <!-- TODO WUI-5429: bullet list
      <wui-btn
        prepend-icon="listM"
        variant="neut"
        state="alpha"
        icon
      >
        <span class="sr-only select-none">{{ t('chat.textFormatBulletList') }}</span>
      </wui-btn>
      -->
      <!-- TODO WUI-5429: quote
      <wui-btn
        prepend-icon="quoteM"
        variant="neut"
        state="alpha"
        icon
      >
        <span class="sr-only select-none">{{ t('chat.textFormatQuote') }}</span>
      </wui-btn>
      -->
      <div :class="formatToggleWrapClass">
        <wui-btn
          prepend-icon="codeM"
          variant="neut"
          state="alpha"
          :title="formatButtonTitle('chat.textFormatCode', 'code')"
          icon
          data-test="chat-text-format-code"
          @mousedown.capture.prevent="onFormatMouseDown('code')"
        >
          <span class="sr-only select-none">{{ t('chat.textFormatCode') }}</span>
        </wui-btn>
        <span
          v-if="activeFormats.code"
          class="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-comp-scroll-def"
        />
      </div>
      <div class="flex items-center">
        <wui-btn
          prepend-icon="eraserM"
          variant="neut"
          state="alpha"
          :title="formatButtonTitle('chat.textFormatClear', 'removeFormat')"
          icon
          data-test="chat-text-format-clear"
          @mousedown.capture.prevent="onFormatMouseDown('removeFormat')"
        >
          <span class="sr-only select-none">{{ t('chat.textFormatClear') }}</span>
        </wui-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WuiBtn } from '@wui/common-library'

import { useLocalization } from '@/shared/composables/localization'

import { getTextFormatShortcutLabel, type TextFormatHotkeyCommand } from '../lib/text-format-hotkeys'
import { ActiveTextFormats, InlineFormatCommand } from '../lib/types'

defineProps<{
  activeFormats: ActiveTextFormats
}>()

const emit = defineEmits<{
  (event: 'prepare'): void
  (event: 'format', command: InlineFormatCommand): void
  (event: 'link'): void
}>()

const { t } = useLocalization()

const formatToggleWrapClass = 'relative -mb-px flex items-center'

const formatButtonTitle = (labelKey: string, command: TextFormatHotkeyCommand) => {
  return `${t(labelKey)} (${getTextFormatShortcutLabel(command)})`
}

const onFormatMouseDown = (command: InlineFormatCommand) => {
  emit('prepare')
  emit('format', command)
}
</script>
